import { useEffect, useRef } from 'react'
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query'
import { useThreadService } from './ThreadServiceProvider'
import { useAgent } from '@copilotkit/react-core/v2'
import * as jsonpatch from 'fast-json-patch'
import type { AgentState } from './types'
import { sortBy } from 'lodash-es'

/**
 * Custom hook for fetching threads data
 */
export const useGetThreads = () => {
    const threadService = useThreadService()

    return useQuery({
        queryKey: ['threads'],
        queryFn: () => threadService.getThreads(),
        refetchOnMount: true,
        // @todo: add proper hooks so we know when we add or delete threads
        staleTime: 0,
    })
}

interface ThreadSummary {
    threadId: string
    lastMessage: string
    progress: number
    lastUpdated: string
    state: AgentState
}

// @todo: fold this into useGetThreads with a filter option
export const useGetUnfinishedThreads = () => {
    const threadService = useThreadService()
    return useQuery({
        queryKey: ['unfinished-threads'],
        queryFn: (): Promise<ThreadSummary[]> => {
            return threadService.getThreads().then((threads) => {
                // @todo: do some actual filtering here
                // const unfinished = threads.filter((thread) => !thread.isCompleted)
                const unfinished = threads
                // @todo: enhance the threadInfo to ThreadSummary
                const summaries: ThreadSummary[] = unfinished.map((thread) => ({
                    ...thread,
                    lastMessage: 'last message unavailable...',
                    progress: Math.floor(Math.random() * 100),
                }))
                // sortby last updated desc
                const orderedSummaries = sortBy(summaries, (t) =>
                    new Date(t.lastUpdated).getTime()
                ).reverse()
                return orderedSummaries
            })
        },
        // @todo: add proper hooks so we know when we add or delete threads
        staleTime: 0,
    })
}

/**
 * Custom hook for deleting threads
 */
export const useDeleteThread = () => {
    const threadService = useThreadService()
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: (threadId: string) => threadService.deleteThread(threadId),
        onSuccess: () => {
            // Invalidate threads query to refetch data
            void queryClient.invalidateQueries({ queryKey: ['threads'] })
        },
        onError: (error) => {
            console.error('Failed to delete thread:', error)
        },
    })
}

type Updater<T> = T | ((prevState: T | undefined) => T)

// oxlint-disable-next-line @typescript-eslint/no-explicit-any
export const useAgentState = <T extends Record<string, any>>(
    threadId: string,
    coAgentOptions: { name: string }
) => {
    const threadService = useThreadService()
    const { agent } = useAgent({ agentId: coAgentOptions.name })
    const agentState = agent.state as T | null | undefined

    const state =
        agentState === null ||
        agentState === undefined ||
        Object.keys(agentState).length === 0
            ? undefined
            : agentState

    const useMutationResult = useMutation({
        mutationFn: (updater: Updater<T>) => {
            // Generate next state and patches using a unified approach
            const nextState =
                typeof updater === 'function' ? updater(state) : updater
            const patches = jsonpatch.compare(state || {}, nextState)

            const result = threadService
                .patchState(threadId, patches)
                .then(() => {
                    return nextState
                })
            return result
        },

        onSuccess: (data) => {
            // If the service call is successful, update the local state
            agent.setState(data)
        },
        onError: (error) => {
            console.error('Failed to update state:', error)
        },
    })

    return { state, ...useMutationResult }
}

/**
 * Trigger backend generation of the thread title (derived from the
 * conversation) and persist it. On success, mirror the new title into local
 * agent state so the UI reflects it without waiting for a state snapshot.
 */
export const useUpdateThreadTitle = (
    threadId: string,
    coAgentOptions: { name: string }
) => {
    const threadService = useThreadService()
    const { agent } = useAgent({ agentId: coAgentOptions.name })

    return useMutation({
        mutationFn: () => threadService.updateThreadTitle(threadId),
        onSuccess: ({ thread_title }) => {
            const current = (agent.state ?? {}) as Partial<AgentState>
            agent.setState({ ...current, threadTitle: thread_title })
        },
        onError: (error) => {
            console.error('Failed to update thread title:', error)
        },
    })
}

// Per-thread auto-title bookkeeping. We title from two signals — the opening
// exchange and the first cart add — and per signal track two facts: whether we
// saw this thread *before* it crossed (a thread that loads already past the
// line, or another thread's state flashing in during navigation, never gets a
// `saw…`, so it can't trigger), and whether we've already titled from it (so
// each titles at most once). `autoTitle` is the last title we wrote, so we
// leave a manual rename or landing title alone.
interface AutoTitleProgress {
    sawBeforeExchange: boolean
    sawEmptyCart: boolean
    titledFromExchange: boolean
    titledFromCart: boolean
    autoTitle?: string
}

/**
 * Auto-title a thread when it crosses EITHER signal — its opening exchange
 * (>=2 messages) completes, OR its cart gains its first audience — at most once
 * per signal, so up to twice per thread: an early conversational title that the
 * first cart add then refines. Coincident crossings share one request (two
 * title writes would race on the backend); a manual rename or landing title is
 * left alone. The shared agent doesn't remount on navigation, so it briefly
 * reports the previous thread's state — the per-signal `saw…` flags ignore that
 * by only titling a thread we watched cross the line ourselves.
 */
export const useAutoThreadTitle = (
    threadId: string,
    coAgentOptions: { name: string }
) => {
    const { agent } = useAgent({ agentId: coAgentOptions.name })
    const { mutate: requestThreadTitle } = useUpdateThreadTitle(
        threadId,
        coAgentOptions
    )

    const state = agent.state as AgentState | null | undefined
    const isRunning = agent.isRunning
    const hasFirstExchange = agent.messages.length >= 2
    const cartHasItems = (state?.audienceConfigurations ?? []).length > 0
    const currentTitle = state?.threadTitle ?? null

    const progressRef = useRef<Map<string, AutoTitleProgress>>(new Map())
    useEffect(() => {
        const progress = progressRef.current.get(threadId) ?? {
            sawBeforeExchange: false,
            sawEmptyCart: false,
            titledFromExchange: false,
            titledFromCart: false,
        }
        progressRef.current.set(threadId, progress)

        // Note when we see this thread below each threshold, so a later crossing
        // reads as a real one rather than a stale or freshly-loaded state.
        if (!hasFirstExchange) progress.sawBeforeExchange = true
        if (!cartHasItems) progress.sawEmptyCart = true

        // Only overwrite a title we wrote ourselves (empty, or still ours).
        if (currentTitle && currentTitle !== progress.autoTitle) return

        const shouldTitleFromExchange =
            hasFirstExchange &&
            progress.sawBeforeExchange &&
            !progress.titledFromExchange
        const shouldTitleFromCart =
            cartHasItems && progress.sawEmptyCart && !progress.titledFromCart
        if (!shouldTitleFromExchange && !shouldTitleFromCart) return

        // Wait for the run to settle: firing mid-run starts a second run
        // ("Thread already running") whose trailing snapshot clobbers our title.
        if (isRunning) return

        if (shouldTitleFromExchange) progress.titledFromExchange = true
        if (shouldTitleFromCart) progress.titledFromCart = true
        requestThreadTitle(undefined, {
            onSuccess: ({ thread_title }) => {
                progress.autoTitle = thread_title
            },
        })
    }, [
        isRunning,
        hasFirstExchange,
        cartHasItems,
        currentTitle,
        threadId,
        requestThreadTitle,
    ])
}

export const useGetSessionIdFromThreadId = (threadId: string) => {
    const threadService = useThreadService()
    return useQuery({
        queryKey: ['getSessionIdFromThreadId', threadId],
        queryFn: () => threadService.getSessionIdFromThreadId(threadId),
        staleTime: 0,
    })
}
